(ns minorodata.components.pipeline-dag.nodes
  "Presentational node cards shared by the global pipelines page
   (`minorodata.components.dependency-graph.dag`) and the scoped pipeline editor
   (`minorodata.components.pipeline-editor.dag`), so the same entity looks the
   same wherever it is seen.

   No handlers and no subscriptions live here — `:root` carries the click handlers
   and visual state the calling wrapper owns, merged onto `ui.dag/node-root`.

   The cards must bottom out in a DOM element, so that a card wrapped in a Radix
   `as-child` trigger (the editor's error tooltip on a transform, the name tooltip on
   a table pill) actually works. Radix clones its child to inject pointer handlers
   and a ref; against a REAGENT COMPONENT element those extra props land beside the
   wrapper's `argv` prop and are silently dropped, so the tooltip never opens. Hence
   two rules, and both are load-bearing:

     - a card is CALLED as a plain function, `(entity-card {...})`, never placed in a
       hiccup vector, and
     - the card in turn CALLS `ui.dag/node-root` rather than nesting it as
       `[ui.dag/node-root ...]`, so what comes back is `[:div ...]` / `[:a ...]`.

   Row layout follows `:in-container?`, stamped on by
   `minorodata.pipeline-dag.layout/size-node` — which also derives the node HEIGHT
   from the same flag, so ELK and the DOM can't disagree:

     in a container  title + last-run          (the box heading names the group)
     loose           title + group + last-run

   …minus the last-run row when `:last-run-row?` is false — a node type with no
   execution history in this view (the sandbox's own transforms/extracts, whose
   status comes from the status bar + failure tooltip instead)."
  (:require
   ["react" :as react]
   [applied-science.js-interop :as j]
   [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
   [minorodata.components.transformations.transforms :refer [execution-time]]
   [reagent.core :as reagent]
   [ui.dag :as ui.dag]
   [ui.utils :refer [clsx]]))


;; =============================================================================
;; Container (cluster) boxes
;; =============================================================================

(def container-color-var
  "Cluster palette var per container node type. Single source of truth for the box
   tint (`container-view`) and the minimap fill (see the pipelines page)."
  {"transform-group" "var(--color-transforms)"
   "source"          "var(--color-extracts)"
   "model-group"     "var(--color-models)"})

(def ^:private container-classes
  "Box border/background + heading text classes per container kind. `:kind` arrives
   as a string (clj->js)."
  {"source"          {:box "border-extracts/40 bg-extracts/5"     :text "text-extracts"}
   "model-group"     {:box "border-models/40 bg-models/5"         :text "text-models"}
   "transform-group" {:box "border-transforms/40 bg-transforms/5" :text "text-transforms"}})

(defn container-view
  "A cluster box behind its member nodes. `pointer-events-none` so clicks fall
   through to the children / canvas."
  [{:keys [data]}]
  (let [{:keys [label kind]} data
        {:keys [box text]} (get container-classes kind (get container-classes "transform-group"))]
    [:div {:class (clsx "w-full h-full rounded-2xl border-2 pointer-events-none overflow-hidden" box)}
     [:div {:class (clsx "px-4 py-3 text-xl font-medium truncate" text)
            :title label}
      label]]))

(defn container-node [^js params]
  (reagent/as-element
   [container-view {:data (js->clj (j/get params :data) :keywordize-keys true)}]))

(def container-node-types
  "The `:node-types` entries that render cluster boxes. Merge into a page's node
   types when `:groups?` layout is available."
  #js {:transform-group container-node
       :source          container-node
       :model-group     container-node})


;; =============================================================================
;; Zoom-out collapse
;; =============================================================================

(def zoom-threshold
  "Below this viewport zoom, entity nodes collapse to a status-color box. Passed to
   `ui.dag/dag` as `:collapse-below-zoom`; nodes read the resulting flag via
   `ui.dag/use-collapsed` (one canvas-level zoom read, not a per-node subscription)."
  0.55)

(defn collapsed-card
  "Status-colour box rendered instead of the card when zoomed out; carries the card's click handlers."
  [{:keys [data status selected highlighted? shape root]}]
  [:div (assoc (dissoc root :opened? :selected? :highlighted? :disabled? :loading? :state :shape :class)
               :data-tooltip (:title data)
               :data-tooltip-desc (:subtitle data)
               :class
               (clsx "w-full h-full shadow-sm border border-black/10 ring-2 ring-transparent"
                     ;; follow the card it replaces, so a pill table doesn't
                     ;; collapse into a square
                     (if (= :pill shape) "rounded-full" "rounded-md")
                     ;; A highlight REPLACES the status fill rather than layering on
                     ;; it: collapsed, this box is the whole node, so a 2px ring on
                     ;; it is invisible at the zoom that triggers the collapse.
                     (if highlighted? "bg-sky-500" (ui.dag/status->color status))
                     {"ring-kleene-secondary-01 dark:ring-kleene-secondary-v2" (and selected (not highlighted?))
                      "ring-sky-300"                                          highlighted?}
                     (:class root)))])


;; =============================================================================
;; Cards
;; =============================================================================

(defn last-run-display
  "Execution-status line for a node: the formatted last run, or a muted
   \"Never executed\" when the entity has never run.

   `truncate` keeps it to ONE line. The run string is variable-length
   (\"Completed 12 Mar 2026, 14:32 in 3 minutes 20 seconds\"), and at `:dense` it has
   only ~238px of text column — wrapping it to a second line would put the card 16px
   over the height `pipeline-dag.layout` declared for it, which is the overflow that
   `one/two/three-row-height` exist to prevent."
  [{:keys [last-run]}]
  [:div {:class "truncate text-xs text-kleene-text-secondary font-[IBM_Plex_Sans_Condensed]"}
   (if last-run
     ;; also on the inner `small` — it renders `block`, so the wrapper's `truncate`
     ;; alone would inherit nowrap and clip it, but with no ellipsis.
     ;; `:plain?` — a Radix tooltip per card is too costly with hundreds on screen
     [execution-time {:last-run last-run :class "truncate" :plain? true}]
     "Never executed")])

(defn entity-card
  "Visual body for transform / extract / model nodes — status bar, icon, title, and
   the group name + last-run split across the subtitle / footer rows according to
   `:in-container?`.

   `:status` / `:last-run` default to the matching `:data` keys, so a page that
   joins run status into node data can omit them, and a page that reads status from
   a per-node subscription can pass it in without re-emitting the whole graph.

   `:last-run-row?` (stamped on `:data` by `size-node`, default true) drops the
   last-run row for a node with no execution history in this view — the card must
   agree with it, because the node HEIGHT was derived from the same flag."
  [{:keys [data root] :as props}]
  (let [{:keys [title subtitle type in-container? opened selected]} data
        status        (get props :status   (:status data))
        last-run      (get props :last-run (:last-run data))
        last-run-row? (get data :last-run-row? true)]
    ;; `node-root` is CALLED, not placed in a hiccup vector — see the ns docstring.
    (ui.dag/node-root
     (merge {:opened? opened :selected? selected} root)
     [ui.dag/node-status-bar {:color (ui.dag/status->color status)}]
     [ui.dag/node-content
      [ui.dag/node-icon [tab-icon {:type type :size 24}]]
      [ui.dag/node-text
       [ui.dag/node-title title]
       ;; In a container the box heading already names the group, so the subtitle row
       ;; carries the last run instead — and drops out along with it.
       (cond
         (not in-container?) [ui.dag/node-subtitle subtitle]
         last-run-row?       [ui.dag/node-subtitle [last-run-display {:last-run last-run}]])]]
     (when (and (not in-container?) last-run-row?)
       [ui.dag/node-footer
        [last-run-display {:last-run last-run}]]))))

(defn table-tooltip-content
  "Hover body for a table PILL — the name and schema the pill has no room for. Both
   pages render tables as pills, so both mount this as their tooltip content; a
   pill without it is an unlabelled icon."
  [{{:keys [title subtitle]} :data}]
  [:div {:class "flex flex-col text-sm"}
   [:p {:class "font-medium whitespace-nowrap text-ellipsis overflow-hidden"}
    title]
   [:p {:class "text-kleene-text-secondary"} subtitle]])

(defn table-card
  "Visual body for table nodes — no status bar (a table has no execution status).
   `:shape :pill` renders the compact icon-only pill both pages use (the name lives
   in a tooltip, see `table-tooltip-content`); `:card` renders the full icon + name
   + schema card.

   A pill draws nothing but an icon, so it carries the name as an `aria-label` — the
   hover tooltip is not an accessible name, and without one a graph of tables is a
   row of identical icons to a screen reader. Deliberately NOT the `title`
   attribute: the browser would draw its own tooltip over ours."
  [{:keys [data root shape] :or {shape :card}}]
  (let [{:keys [title subtitle type opened selected disabled]} data
        pill? (= :pill shape)]
    ;; `node-root` is CALLED, not placed in a hiccup vector — see the ns docstring.
    (ui.dag/node-root
     (merge {:shape     (if pill? :pill :rect)
             :opened?   opened
             :selected? selected
             :disabled? disabled}
            (when pill? {:aria-label        title
                         :data-tooltip      title
                         :data-tooltip-desc subtitle})
            root)
     [ui.dag/node-content
      [ui.dag/node-icon [tab-icon {:type type :size 24}]]
      (when-not pill?
        [ui.dag/node-text
         [ui.dag/node-title title]
         [ui.dag/node-subtitle subtitle]])])))


;; =============================================================================
;; xyflow node wrapper
;; =============================================================================

(defn entity-node
  "Builds an xyflow node component, delegating the card to the reagent component
   `inner` (which receives `{:data <node data>}`). The js→clj conversion is
   memoized on the raw js data, so a node only re-converts when xyflow actually
   hands it new data.

   `inner` is rendered as a plain reagent component (not `:f>`) so `re-posh`
   subscriptions inside it stay reactive; the zoom-collapse flag is read here and
   passed down as `:collapsed?`."
  [inner]
  (fn [^js params]
    (let [js-data    (j/get params :data)
          data       (react/useMemo #(js->clj js-data :keywordize-keys true)
                                    #js [js-data])
          collapsed? (ui.dag/use-collapsed)]
      (reagent/as-element
       ;; handles stay mounted when collapsed: xyflow finds them by DOM query
       [ui.dag/node-handles
        [inner {:data data :collapsed? collapsed?}]]))))
